import React, { createContext, useState, useContext, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { clearAllCache } from '../hooks/useCache';

const AuthContext = createContext(null);

// Local storage keys
const USER_EMAIL_KEY = 'recapify_userEmail';
const USER_DISPLAY_NAME_KEY = 'recapify_displayName';
const AUTH_STATUS_KEY = 'recapify_authStatus';
const PAYMENT_STATUS_KEY = 'recapify_paymentStatus';
const IDENTITY_ID = 'recapify_identityId';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [trialStatus, setTrialStatus] = useState({
    isTrialUser: false,
    needsPayment: null,
    creditRemaining: 0
  });

  // Load cached user data from local storage
  useEffect(() => {
    const cachedEmail = localStorage.getItem(USER_EMAIL_KEY);
    const cachedDisplayName = localStorage.getItem(USER_DISPLAY_NAME_KEY);
    const cachedAuthStatus = localStorage.getItem(AUTH_STATUS_KEY) === 'true';
    const cachedPaymentStatus = localStorage.getItem(PAYMENT_STATUS_KEY) === 'true';
    
    if (cachedEmail) setUserEmail(cachedEmail);
    if (cachedDisplayName) setDisplayName(cachedDisplayName);
    if (cachedAuthStatus) setIsAuthenticated(cachedAuthStatus);
    if (cachedPaymentStatus) setHasPaymentMethod(cachedPaymentStatus);
    
    // Still need to verify with server
    checkAuthState();
  }, []);

  // Update local storage when user data changes
  useEffect(() => {
    if (userAttributes?.email) {
      const email = userAttributes.email;
      const name = userAttributes.name || email.split('@')[0]; // Use name attribute or fallback to username
      
      setUserEmail(email);
      setDisplayName(name);
      
      localStorage.setItem(USER_EMAIL_KEY, email);
      localStorage.setItem(USER_DISPLAY_NAME_KEY, name);
      localStorage.setItem(AUTH_STATUS_KEY, 'true');
    }
  }, [userAttributes]);

  const checkAuthState = async () => {
    setIsLoading(true);
    try {
      const user = await getCurrentUser();
      setIsAuthenticated(true);
      setUserInfo(user);
      localStorage.setItem(AUTH_STATUS_KEY, 'true');
      
      // Check if we already have cached user data
      const cachedEmail = localStorage.getItem(USER_EMAIL_KEY);
      
      // Only fetch attributes if we don't have cached data
      if (!cachedEmail) {
        try {
          const attributes = await fetchUserAttributes();
          setUserAttributes(attributes);
        } catch (attributeError) {
          console.error('Error fetching user attributes:', attributeError);
        }
      }
      
      // Check payment method status from backend
      await checkPaymentStatus();
    } catch (error) {
      clearUserData();
    } finally {
      setIsLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      // Get ID token to check custom claims
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      
      // Check for needsPayment claim from pre-token generation trigger
      const needsPayment = session.tokens?.idToken?.payload?.needsPayment;
      const trialPhase = session.tokens?.idToken?.payload?.trialPhase;
      const creditRemaining = session.tokens?.idToken?.payload?.creditRemaining;
      
      console.log('Token claims:', { needsPayment, trialPhase, creditRemaining });
      
      // Update trial status state
      setTrialStatus({
        isTrialUser: trialPhase === 'true',
        needsPayment: needsPayment,
        creditRemaining: creditRemaining ? parseFloat(creditRemaining) : 0
      });
      
      // If claim says new user, force payment setup
      if (needsPayment === 'new_user') {
        console.log('New user needs to choose payment or trial');
        setHasPaymentMethod(false);
        localStorage.setItem(PAYMENT_STATUS_KEY, 'false');
        return;
      }
      
      // If claim says false, user has credit or payment
      if (needsPayment === 'false') {
        // Still check backend to get actual payment method status
        // (user might have credit but no payment method yet)
      }

      // Check localStorage first for payment method
      const cachedStatus = localStorage.getItem(PAYMENT_STATUS_KEY);
      if (cachedStatus === 'true') {
        console.log('Using cached payment status: true');
        setHasPaymentMethod(true);
        return;
      }

      // If not cached or false, check with backend
      console.log('Checking payment status with backend');
      const { LambdaClient, InvokeCommand } = await import('@aws-sdk/client-lambda');
      const config = await import('../config');
      
      const lambda = new LambdaClient({
        region: config.default.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.default.StripePaymentFunctionArn,
        Payload: JSON.stringify({ action: 'get_payment_info' })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));
      
      if (result.statusCode === 200) {
        const data = JSON.parse(result.body);
        const hasPayment = data.hasPaymentMethod || false;
        console.log('Backend payment status:', hasPayment);
        setHasPaymentMethod(hasPayment);
        localStorage.setItem(PAYMENT_STATUS_KEY, hasPayment.toString());
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      // Don't fail auth check if payment check fails
    }
  };

  const clearUserData = () => {
    setIsAuthenticated(false);
    setUserInfo(null);
    setUserAttributes(null);
    setUserEmail('');
    setDisplayName('');
    setHasPaymentMethod(false);
    
    localStorage.removeItem(USER_EMAIL_KEY);
    localStorage.removeItem(USER_DISPLAY_NAME_KEY);
    localStorage.removeItem(AUTH_STATUS_KEY);
    localStorage.removeItem(PAYMENT_STATUS_KEY);
    localStorage.removeItem(IDENTITY_ID);
    clearAllCache();
  };

  const setupPaymentMethod = async (success) => {
    if (success) {
      console.log('setupPaymentMethod called, re-checking payment status from backend');
      // Re-check payment status from backend to get accurate state
      // (trial users will have hasPaymentMethod: false, paid users: true)
      await checkPaymentStatus();
      
      // Get redirect destination and navigate
      const redirectPath = sessionStorage.getItem('recapify_redirect_after_setup') || '/recordings';
      sessionStorage.removeItem('recapify_redirect_after_setup');
      console.log(`Setup complete, redirecting to: ${redirectPath}`);
      
      // Return the redirect path so the component can navigate
      return redirectPath;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      clearUserData();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        isLoading, 
        userInfo,
        userAttributes,
        userEmail,
        displayName,
        hasPaymentMethod,
        trialStatus,
        checkAuthState,
        handleSignOut,
        setupPaymentMethod
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
